import React, { useRef, useEffect, useState, useContext } from 'react';
import { Typography, IconButton, Input, Button, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import Modal from 'styled-react-modal';
import { Formik, FieldArray, ErrorMessage } from 'formik';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { MdRemove } from 'react-icons/md';
import { GrFormAdd } from 'react-icons/gr';
import { RiImageAddFill } from 'react-icons/ri';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { useLoading, ThreeDots } from '@agney/react-loading';
import clsx from 'clsx';
import { AuthContext } from '../../services/AuthContext';
import { addNewRecipe } from '../../redux/actions/recipes';
import { recipeSchema } from '../../services/recipeSchema';
import { useStyles } from './styles';
import './styles.sass';

// firebase
import { doc, setDoc } from 'firebase/firestore/lite';
import { db } from '../../services';

const InsertionDialog = ({ open, setOpen, activeScreen, setActiveScreen }) => {
    const prevState = useRef(null);
    const [image, setImage] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const [prevScreen, setPrevScreen] = useState('');
    const { user } = useContext(AuthContext);
    const classes = useStyles();
    const dispatch = useDispatch();
    const { containerProps, indicatorEl } = useLoading({
        loading: true,
        indicator: <ThreeDots width="30" color="#FFFFFF" />,
    });

    const handleClose = () => {
        setOpen(false);
        setActiveScreen(prevScreen);
        if (image)
            setImage(null);
    }

    const handleImageUpload = (files) => {
        setImage(files[0]);
    }

    const handleAddDocument = async (newRecipe) => {
        try {
            await setDoc(doc(db, 'recipes', newRecipe.id), newRecipe); // Add new doc
        }
        catch (error) {
            console.log(error.message);
        }
        finally {
            dispatch(addNewRecipe(newRecipe)); // Update store
            handleClose();
            setTimeout(() => {
                setDisabled(false);
            }, 500);
        }
    }

    const onAddNewRecipe = async (newRecipe) => {
        setDisabled(true);
        if (newRecipe.time.value > 1 && newRecipe.time.unit === 'דקה')
            newRecipe.time.unit = 'דקות';
        else
            if (newRecipe.time.value > 1 && newRecipe.time.unit === 'שעה')
                newRecipe.time.unit = 'שעות';
        // Generate new recipe constant data
        newRecipe.id = uuidv4();
        newRecipe.owner = user.email;
        newRecipe.created = new Date();
        newRecipe.favorite = false;
        // Check if the user has uploaded an image
        if (image) {
            const data = new FormData();
            data.append('file', image);
            data.append('upload_preset', 'cookEat');
            data.append('cloud_name', process.env.REACT_APP_CLOUDINARY_KEY);
            fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_KEY}/image/upload`, {
                method: 'POST',
                body: data
            })
                .then(res => res.json())
                .then(data => {
                    newRecipe.image = {
                        url: data.url,
                        public_id: data.public_id
                    };
                    handleAddDocument(newRecipe);
                })
                .catch((error) => console.log("error on upload image:", error.message));
        }
        else {
            newRecipe.image = {
                url: 'https://res.cloudinary.com/ddofzlgdu/image/upload/v1647108359/pexels-curtis-adams-10099256_dgyisj.jpg',
                public_id: null
            };
            handleAddDocument(newRecipe);
        }
    }

    useEffect(() => {
        prevState.current = activeScreen;
        if (prevState.current !== 'add')
            setPrevScreen(prevState.current);
    }, [activeScreen]);

    return (
        <StyledModal
            isOpen={open}
            onBackgroundClick={handleClose}
            onEscapeKeydown={handleClose}
        >
            <div className="header">
                <Typography className={classes.text} variant="h6">הוספת מתכון חדש</Typography>
                <label htmlFor="file-upload" className="custom-file-upload">
                    {!image ?
                        <RiImageAddFill className="icon" />
                        :
                        <img className="preview" src={URL.createObjectURL(image)} alt="Error" />
                    }
                </label>
                <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={(event) => handleImageUpload(event.target.files)}
                />
            </div>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values) => onAddNewRecipe(values)}
                validationSchema={recipeSchema}
            >
                {({ handleChange, handleSubmit, values, errors, touched, resetForm }) => {
                    return (
                        <div className="form">
                            <div className="text-input-wrapper mt0">
                                <Typography variant="subtitle1" className={classes.text}>שם המתכון</Typography>
                                <Input
                                    required
                                    autoFocus
                                    value={values.name}
                                    onChange={handleChange('name')}
                                    className={classes.input}
                                    variant="outlined"
                                    placeholder='איך קוראים למנה?'
                                    disableUnderline
                                />
                            </div>
                            <ErrorMessage
                                name='name'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* quantity */}
                            <div className="text-input-wrapper">
                                <Typography variant="subtitle1" className={classes.text}>כמות מנות</Typography>
                                <Input
                                    required
                                    value={values.quantity}
                                    onChange={handleChange('quantity')}
                                    className={classes.input}
                                    variant="outlined"
                                    placeholder='לכמה אנשים הוא מספיק?'
                                    disableUnderline
                                />
                            </div>
                            <ErrorMessage
                                name='quantity'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* category */}
                            <div className="text-input-wrapper">
                                <Typography variant="subtitle1" className={classes.text}>קטגוריה</Typography>
                                <Input
                                    required
                                    value={values.category}
                                    onChange={handleChange('category')}
                                    className={classes.input}
                                    variant="outlined"
                                    placeholder='לאיזו קטגוריה הוא שייך?'
                                    disableUnderline
                                />
                            </div>
                            <ErrorMessage
                                name='category'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* time */}
                            <div className="text-input-wrapper">
                                <Typography variant="subtitle1" className={classes.text}>זמן הכנה</Typography>
                                <div className="time-wrapper">
                                    <Input
                                        required
                                        value={values.time.value}
                                        onChange={handleChange('time.value')}
                                        className={classes.input}
                                        variant="outlined"
                                        placeholder='בכמה זמן מכינים אותו?'
                                        disableUnderline
                                    />
                                    <RadioGroup
                                        row
                                        defaultValue='דקה'
                                        onChange={handleChange('time.unit')}
                                        className={classes.radioGroup}
                                    >
                                        <FormControlLabel
                                            value='דקה'
                                            control={<Radio />}
                                            label={<Typography className={classes.text}>דקה</Typography>}
                                        />
                                        <FormControlLabel
                                            value='שעה'
                                            control={<Radio />}
                                            label={<Typography className={classes.text}>שעה</Typography>}
                                        />
                                    </RadioGroup>
                                </div>
                            </div>
                            <ErrorMessage
                                name='category'
                                render={(message) => {
                                    return (
                                        <div className="error-container">
                                            <WarningAmberRoundedIcon className="icon" />
                                            <Typography className={classes.error}>{message}</Typography>
                                        </div>
                                    )
                                }}
                            />
                            {/* ingredients */}
                            <Typography variant='h6' className={clsx(classes.text, classes.label)}>מרכיבים</Typography>
                            <FieldArray
                                name='ingredients'
                                render={(arrayHelpers) => {
                                    const ingredients = values.ingredients;
                                    return (
                                        ingredients.map((ingredient, index) => {
                                            return (
                                                <div key={index}>
                                                    <div className='dynamic-input-wrapper'>
                                                        <div className='text-input-wrapper mb0'>
                                                            <Input
                                                                placeholder={`מרכיב ${index + 1}`}
                                                                value={values.ingredients[index].title}
                                                                onChange={handleChange(`ingredients.${index}.title`)}
                                                                className={classes.input}
                                                                variant="outlined"
                                                                disableUnderline
                                                            />
                                                        </div>
                                                        <div className='text-input-wrapper mb0 mh5'>
                                                            <Input
                                                                placeholder='כמות'
                                                                value={values.ingredients[index].amount}
                                                                onChange={handleChange(`ingredients.${index}.amount`)}
                                                                className={classes.input}
                                                                variant="outlined"
                                                                disableUnderline
                                                            />
                                                        </div>
                                                        <div className='text-input-wrapper mb0'>
                                                            <Input
                                                                placeholder='יחידה'
                                                                value={values.ingredients[index].unit}
                                                                onChange={handleChange(`ingredients.${index}.unit`)}
                                                                className={classes.input}
                                                                variant="outlined"
                                                                disableUnderline
                                                            />
                                                        </div>
                                                        {ingredients.length - 1 === index &&
                                                            <IconButton
                                                                onClick={() => arrayHelpers.push({ title: '', amount: '', unit: '' })}
                                                                className='action-button'
                                                                size='small'
                                                            >
                                                                <GrFormAdd className='add' />
                                                            </IconButton>
                                                        }
                                                        {ingredients.length > 1 &&
                                                            <IconButton
                                                                onClick={() => arrayHelpers.remove(index)}
                                                                className='action-button'
                                                                size='small'
                                                            >
                                                                <MdRemove className='remove' />
                                                            </IconButton>
                                                        }
                                                    </div>
                                                    {touched.ingredients && errors.ingredients && errors.ingredients[index] &&
                                                        <ErrorMessage
                                                            name={`ingredients.${index}.title`}
                                                            render={(message) => {
                                                                return (
                                                                    <div className="error-container">
                                                                        <WarningAmberRoundedIcon className="icon" />
                                                                        <Typography className={classes.error}>{message}</Typography>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    }
                                                    {touched.ingredients && errors.ingredients && errors.ingredients[index] &&
                                                        <ErrorMessage
                                                            name={`ingredients.${index}.amount`}
                                                            render={(message) => {
                                                                return (
                                                                    <div className="error-container">
                                                                        <WarningAmberRoundedIcon className="icon" />
                                                                        <Typography className={classes.error}>{message}</Typography>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    }
                                                    {touched.ingredients && errors.ingredients && errors.ingredients[index] &&
                                                        <ErrorMessage
                                                            name={`ingredients.${index}.unit`}
                                                            render={(message) => {
                                                                return (
                                                                    <div className="error-container">
                                                                        <WarningAmberRoundedIcon className="icon" />
                                                                        <Typography className={classes.error}>{message}</Typography>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    }
                                                </div>
                                            )
                                        })
                                    );
                                }}
                            />
                            {/* directions */}
                            <Typography variant='h6' className={clsx(classes.text, classes.label)}>הוראות הכנה</Typography>
                            <FieldArray
                                name='directions'
                                render={(arrayHelpers) => {
                                    const directions = values.directions;
                                    return (
                                        directions.map((direction, index) => (
                                            <div style={{ width: '100%' }} key={index}>
                                                <div className="dynamic-input-wrapper">
                                                    <div className='text-input-wrapper mb0'>
                                                        <Input
                                                            mul
                                                            placeholder={`הוראה ${index + 1}`}
                                                            value={values.directions[index]}
                                                            onChange={handleChange(`directions.${index}`)}
                                                            className={classes.input}
                                                            variant="outlined"
                                                            disableUnderline
                                                            multiline
                                                        />
                                                    </div>
                                                    {directions.length - 1 === index &&
                                                        <IconButton
                                                            onClick={() => arrayHelpers.push('')}
                                                            className='action-button'
                                                            size='small'
                                                        >
                                                            <GrFormAdd className='add' />
                                                        </IconButton>
                                                    }
                                                    {directions.length > 1 &&
                                                        <IconButton
                                                            onClick={() => arrayHelpers.remove(index)}
                                                            className='action-button'
                                                            size='small'
                                                        >
                                                            <MdRemove className='remove' />
                                                        </IconButton>
                                                    }
                                                </div>
                                                {touched.directions && errors.directions &&
                                                    <ErrorMessage
                                                        name={`directions.${index}`}
                                                        render={(message) => {
                                                            return (
                                                                <div className="error-container">
                                                                    <WarningAmberRoundedIcon className="icon" />
                                                                    <Typography className={classes.error}>{message}</Typography>
                                                                </div>
                                                            )
                                                        }}
                                                    />
                                                }
                                            </div>
                                        ))
                                    );
                                }}
                            />
                            <div className="buttons">
                                <Button
                                    onClick={handleSubmit}
                                    className={clsx(classes.button, classes.add)}
                                    disabled={disabled}
                                >
                                    {!disabled ?
                                        'הוספה'
                                        :
                                        <section className="indicator" {...containerProps}>
                                            {indicatorEl}
                                        </section>
                                    }
                                </Button>
                                {!disabled &&
                                    <Button
                                        onClick={() => {
                                            resetForm();
                                            setImage(null);
                                        }}
                                        className={clsx(classes.button, classes.clear)}
                                    >
                                        ניקוי
                                    </Button>
                                }
                            </div>
                        </div>
                    );
                }}
            </Formik>
        </StyledModal>
    )
}

const StyledModal = Modal.styled`
    width: 450px;
    height: 450px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 25px;
    background-color: #000f14;
    cursor: default;

    @media (max-width: 960px) {
        height: 100%;
    }

    @media (max-width: 400px) {
        width: 100%;
    }
`;

const initialValues = {
    name: '',
    quantity: '',
    category: '',
    time: {
        value: '',
        unit: 'דקה'
    },
    ingredients: [{
        title: '',
        amount: '',
        unit: ''
    }],
    directions: ['']
};

export default InsertionDialog;